import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import { ToggleSwitch, tokens } from 'components/v3';

// ToggleSwitch has no `disabled` prop (it's a byte-for-byte port), so a
// disabled switch sits in a disabled <fieldset>, which switches the button
// off natively for mouse and keyboard alike.

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const Switch = ({ on, onChange, label, disabled, size }) => {
  const toggle = (
    <ToggleSwitch
      on={on}
      size={size}
      label={label}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onChange(!on);
      }}
    />
  );
  if (!disabled) return toggle;
  return (
    <fieldset
      disabled
      style={{
        border: 0,
        padding: 0,
        margin: 0,
        minWidth: 0,
        display: 'inline-flex',
        opacity: 0.5,
      }}
    >
      <legend style={VISUALLY_HIDDEN}>{label}</legend>
      {toggle}
    </fieldset>
  );
};

Switch.propTypes = {
  on: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md']),
};

Switch.defaultProps = { disabled: false, size: 'sm' };

// A settings row: label and one-line explanation, switch on the right.
const ToggleRow = ({ label, sub, on, onChange, disabled, first, help }) => {
  const t = tokens(useTheme());
  return (
    <div
      data-help={help || undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '11px 0',
        borderTop: first ? 'none' : '1px solid ' + t.line,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: t.text }}>{label}</div>
        {sub ? (
          <div style={{ fontSize: 13, color: t.faint, marginTop: 2 }}>
            {sub}
          </div>
        ) : null}
      </div>
      <Switch on={on} onChange={onChange} label={label} disabled={disabled} />
    </div>
  );
};

ToggleRow.propTypes = {
  label: PropTypes.string.isRequired,
  sub: PropTypes.string,
  on: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  first: PropTypes.bool,
  // A tooltip REGISTRY id (help/tooltips.js) for the whole row.
  help: PropTypes.string,
};

ToggleRow.defaultProps = {
  sub: null,
  disabled: false,
  first: false,
  help: null,
};

export default ToggleRow;
